import { IGetAllProductsResult } from '../actions/create-order/get-all-products.action';
import { IPostCartItems } from '../actions/create-order/post-cart.action';
import {
    IPostCreateOrderData,
    IPostCreateOrderResponseData,
    IPostCreateOrderResponseOrderData
} from '../actions/create-order/post-create-order.action';
import { IOrderHeader } from '../modules/lxs-create-order/lxs-create-order.data';
import { mapBusinessUnitToText } from './map-business-unit-to-text';

type EtrackerProductInput = IGetAllProductsResult | IPostCartItems;
declare global {
    interface Window {
        _etrackerOnReady?: any;
        etCommerce: any;
        et_seg1?: string;
    }
}
interface IEtrackerProduct {
    id: string;
    name: string;
    price: string;
    currency: string;
    category?: string[] | string;
}
interface IEtrackerOrder {
    orderNumber: string;
    status: string;
    orderPrice: string;
    currency: string;
    basket: IEtrackerBasket;
    customerGroup: string;
    deliveryConditions: string;
    paymentConditions: string;
}
interface IEtrackerBasket {
    id: string;
    products: IEtrackerOrderProduct[];
}
interface IEtrackerOrderProduct {
    product: IEtrackerProduct;
    quantity: number;
}

const DEFAULT_CURRENCY = 'USD';

enum eCommerceAction {
    Insert = 'insertToBasket',
    Remove = 'removeFromBasket',
    Order = 'order'
}

const isGetAllProductsResult = (data: EtrackerProductInput): data is IGetAllProductsResult => {
    return (data as IGetAllProductsResult).DivisionText !== undefined;
};

const trackOnReady = (callback: () => void): void => {
    window._etrackerOnReady = typeof window._etrackerOnReady === 'undefined' ? [] : window._etrackerOnReady;

    window._etrackerOnReady.push(callback);
};

const convertOrderLineToEtrackerProduct = (
    responseData: IPostCreateOrderResponseOrderData,
    orderData: IPostCreateOrderData
): IEtrackerProduct[] => {
    return responseData.OrderLines.map(responseObject => {
        return {
            id: responseObject.ProductNumber,
            name: responseObject.ProductName,
            price: '1',
            currency: DEFAULT_CURRENCY
        };
    });
};

const convertProductToEtrackerProduct = (product: EtrackerProductInput, header?: IOrderHeader): IEtrackerProduct => {
    let divisionText: string | undefined;

    if (isGetAllProductsResult(product)) {
        divisionText = mapBusinessUnitToText(product.Division);
    } else if (header?.Division) {
        divisionText = mapBusinessUnitToText(header.Division);
    }

    return {
        id: product.ProductNumber,
        name: product.ProductName,
        price: '1',
        currency: DEFAULT_CURRENCY,
        category: [divisionText || '']
    };
};

const convertOrderToEtrackerOrder = (orderData: IPostCreateOrderData, responseData: IPostCreateOrderResponseData): IEtrackerOrder => {
    const etrackerProducts: IEtrackerProduct[] = convertOrderLineToEtrackerProduct(responseData.Order, orderData);

    const etrackerOrderProducts: IEtrackerOrderProduct[] = etrackerProducts.map(product => {
        return {
            product: product,
            quantity: 1
        } as IEtrackerOrderProduct;
    });

    return {
        orderNumber: responseData.Order.OrderNumber,
        status: 'sale',
        orderPrice: '1',
        currency: DEFAULT_CURRENCY,
        basket: {
            id: responseData.Order.OrderNumber,
            products: etrackerOrderProducts
        },
        customerGroup: mapBusinessUnitToText(orderData.Order.Division),
        deliveryConditions: '',
        paymentConditions: ''
    };
};

const isEtrackerReady = (): boolean => typeof window.etCommerce?.sendEvent === 'function' && !Array.isArray(window.etCommerce);

const _sendProductIfReady = (action: eCommerceAction, payload: IEtrackerProduct, quantity?: number, et_seg1?: string): void => {
    if (!isEtrackerReady()) {
        return;
    }
    if (typeof quantity === 'number') {
        window.et_seg1 = et_seg1;
        window.etCommerce.sendEvent(action, payload, quantity, et_seg1);
    }
};

const _sendOrderIfReady = (action: eCommerceAction, payload: IEtrackerOrder, et_seg1?: string): void => {
    if (!isEtrackerReady()) {
        return;
    }
    window.et_seg1 = et_seg1;
    window.etCommerce.sendEvent(action, payload, et_seg1);
};

export const trackInsertToBasket = (product: IPostCartItems | IGetAllProductsResult) => {
    const convertedProduct = convertProductToEtrackerProduct(product);
    let et_seg1: string | undefined = 'NA';
    if (isGetAllProductsResult(product)) {
        et_seg1 = mapBusinessUnitToText(product.Division);
    }
    _sendProductIfReady(eCommerceAction.Insert, convertedProduct, 1, et_seg1);
};

export const trackRemoveSingleItemFromBasket = (product: IPostCartItems | IGetAllProductsResult, header?: IOrderHeader) => {
    const convertedProduct = convertProductToEtrackerProduct(product);
    let et_seg1: string | undefined = 'NA';
    if (header?.Division) {
        et_seg1 = mapBusinessUnitToText(header.Division);
    }
    _sendProductIfReady(eCommerceAction.Remove, convertedProduct, 1, et_seg1);
};

export const trackRemoveItemsFromBasket = (items: IPostCartItems[], header: IOrderHeader) => {
    items.forEach(item => {
        const convertedProduct = convertProductToEtrackerProduct(item, header);
        let et_seg1: string | undefined = 'NA';
        if (header?.Division) {
            et_seg1 = mapBusinessUnitToText(header.Division);
        }
        _sendProductIfReady(eCommerceAction.Remove, convertedProduct, 1, et_seg1);
    });
};

// et_seg1 = Division/BusinessUnit
export const setGlobalDimensions = (dimensionValues: string[]) => {
    dimensionValues.map((value: string, index: number) => {
        const dimensionIndex = index + 1;
        (window as any)[`et_seg${dimensionIndex}`] = value;
    });
};

export const trackSubmittedOrder = (orderData: IPostCreateOrderData, responseData: IPostCreateOrderResponseData) => {
    const convertedOrder = convertOrderToEtrackerOrder(orderData, responseData);
    const et_seg1 = convertedOrder.customerGroup ?? 'NA';
    trackOnReady(() => {
        _sendOrderIfReady(eCommerceAction.Order, convertedOrder, et_seg1);
    });
};
